import React, { useState, useEffect } from 'react'
import {
  Table, Card, Input, Select, DatePicker, Button, Space, Tag, Drawer, Typography, Tooltip, Popconfirm, App
} from 'antd'
import {
  SearchOutlined, ExportOutlined, EyeOutlined, DollarOutlined, DeleteOutlined, ReloadOutlined,
  UserOutlined, MessageOutlined, RobotOutlined, ClockCircleOutlined, TagOutlined,
  CloudOutlined, CameraOutlined, NodeIndexOutlined, KeyOutlined
} from '@ant-design/icons'
import { conversationApi } from '../../services/api'
import MessageContent from '../../components/MessageContent'
import dayjs from 'dayjs'

const { RangePicker } = DatePicker
const { Option } = Select
const { Text, Paragraph } = Typography

interface Conversation {
  id: number
  user_id: string
  chat_id: string
  model: string
  title: string
  message_count: number
  last_message: string
  total_tokens: number
  created_at: number
  updated_at: number
  // 🔥 新增：Canvas上下文相关字段
  context_mode?: 'canvas-based' | 'chat-based'
  canvas_nodes_count?: number
  latest_result_id?: string
  is_canvas_session?: boolean
  context_identifier?: string
  // 🔥 新增：客户端和画布相关字段
  client_type?: string
  client_version?: string
  canvas_type?: 'real' | 'virtual'
  canvas_id?: string
  has_images?: boolean
  // 🔥 新增：来自messages表的费用字段
  total_cost?: number
  input_cost?: number
  output_cost?: number
  billing?: {
    total_cost: number
    input_cost?: number
    output_cost?: number
    currency: string
    estimated?: boolean
    source?: string
  }
}

interface Message {
  id: number
  role: 'user' | 'assistant' | 'system'
  content: string | any[]
  tokens: number
  total_tokens?: number  // 添加 total_tokens 属性
  created_at: number
  // 🔥 新增：Canvas相关字段
  is_canvas_node?: boolean
  result_id?: string
  context_group?: string
  message_type?: 'canvas-node' | 'chat-message'
  // 🔥 新增：图片相关字段
  has_images?: boolean
  image_count?: number
  images?: Array<{
    index: number
    type: string
    url?: string
    storageKey?: string
  }>
  content_type?: string
  parsed_content?: string | any[]
  // 🔥 新增：来自messages表的费用字段
  total_cost?: number
  input_cost?: number
  output_cost?: number
  // 🔥 新增：计费相关字段
  billing?: {
    total_cost: number
    input_cost: number
    output_cost: number
    currency: string
  }
}

const LatestSessionsPage: React.FC = () => {
  const { message } = App.useApp()
  const [conversations, setConversations] = useState<Conversation[]>([])
  const [loading, setLoading] = useState(false)
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  })

  // 🔥 修复：增强筛选条件，支持上下文感知筛选和排序
  const [filters, setFilters] = useState({
    userId: '',
    model: '',
    keyword: '',
    dateRange: null as [dayjs.Dayjs, dayjs.Dayjs] | null,
    // 🔥 新增：上下文相关筛选
    contextMode: 'all', // 'all', 'canvas-based', 'chat-based'
    canvasType: 'all', // 'all', 'real', 'random'
    clientType: 'all', // 'all', 'nextchat', 'curl', 'browser', etc.
    hasImages: 'all', // 'all', 'yes', 'no'
    // 🔥 新增：排序选项
    sortBy: 'context_activity', // 'context_activity', 'updated_at', 'canvas_nodes', 'tokens', 'cost'
    sortOrder: 'desc' // 'asc', 'desc'
  })

  // 会话详情抽屉
  const [detailVisible, setDetailVisible] = useState(false)
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null)
  const [messages, setMessages] = useState<Message[]>([])
  const [detailLoading, setDetailLoading] = useState(false)

  // 🔥 修复：优化数据获取，支持上下文感知筛选和排序
  const fetchConversations = async (page = 1) => {
    try {
      setLoading(true)
      const params = {
        page,
        limit: pagination.pageSize,
        userId: filters.userId || undefined,
        model: filters.model || undefined,
        keyword: filters.keyword || undefined,
        startDate: filters.dateRange?.[0]?.valueOf(),
        endDate: filters.dateRange?.[1]?.valueOf(),
        // 🔥 修复：上下文感知参数（保持默认值，不设置为undefined）
        contextMode: filters.contextMode,
        canvasType: filters.canvasType,
        clientType: filters.clientType !== 'all' ? filters.clientType : undefined,
        hasImages: filters.hasImages !== 'all' ? (filters.hasImages === 'yes') : undefined,
        // 🔥 新增：排序参数
        sortBy: filters.sortBy,
        sortOrder: filters.sortOrder
      }

      const response = await conversationApi.getConversations(params)
      if (response.success && response.data) {
        // 🔥 修复：直接使用conversations查询中已包含的费用信息，避免重复计算
        const conversationsWithBilling = response.data.map((conv: Conversation) => {
          // 直接使用后端查询中已经计算好的费用信息
          const billing = {
            total_cost: Number(conv.total_cost) || 0,
            input_cost: Number(conv.input_cost) || 0,
            output_cost: Number(conv.output_cost) || 0,
            total_tokens: Number(conv.total_tokens) || 0,
            currency: 'USD',
            estimated: false,
            source: 'billing_records'
          }

          // 🔥 修复：只在费用为0或异常时输出日志
          if (billing.total_cost === 0 && (conv.total_tokens > 0 || conv.message_count > 0)) {
            console.warn(`⚠️ 会话 ${conv.id} 费用为0但有token使用: tokens=${conv.total_tokens}, messages=${conv.message_count}`)
          }

          return {
            ...conv,
            billing
          }
        })

        setConversations(conversationsWithBilling)
        setPagination(prev => ({
          ...prev,
          current: page,
          total: response.pagination?.total || 0
        }))
      } else {
        message.error('获取会话列表失败')
      }
    } catch (error) {
      console.error('获取会话列表失败:', error)
      message.error('获取会话列表失败')
    } finally {
      setLoading(false)
    }
  }

  const fetchConversationDetail = async (conversation: Conversation) => {
    try {
      setDetailLoading(true)

      // 🔥 修复：只获取会话详情，费用信息已在conversation对象中
      const detailResponse = await conversationApi.getConversationDetail(conversation.user_id, conversation.chat_id)

      if (detailResponse.success) {
        setMessages(detailResponse.data.messages || [])

        // 直接使用已有的费用信息
        setSelectedConversation(conversation)
        setDetailVisible(true)
      } else {
        message.error('获取会话详情失败')
      }
    } catch (error) {
      console.error('获取会话详情失败:', error)
      message.error('获取会话详情失败')
    } finally {
      setDetailLoading(false)
    }
  }

  const handleExport = () => {
    const params = {
      userId: filters.userId || undefined,
      model: filters.model || undefined,
      keyword: filters.keyword || undefined,
      startDate: filters.dateRange?.[0]?.valueOf(),
      endDate: filters.dateRange?.[1]?.valueOf()
    }
    conversationApi.exportConversations(params)
  }

  // 🔥 新增：删除会话功能
  const handleDeleteConversation = async (conversation: Conversation) => {
    try {
      const response = await conversationApi.deleteConversation(conversation.user_id, conversation.chat_id)
      if (response.success) {
        message.success('会话删除成功')
        // 重新加载数据
        fetchConversations(pagination.current)
      } else {
        message.error('删除失败')
      }
    } catch (error) {
      console.error('删除会话失败:', error)
      message.error('删除失败')
    }
  }

  useEffect(() => {
    fetchConversations()
  }, [filters])

  // 🔥 修复：增强 formatCost 函数的类型安全性
  const formatCost = (cost: any) => {
    // 🔥 修复：确保输入是数字类型
    const numCost = Number(cost);
    if (isNaN(numCost) || cost === null || cost === undefined) {
      return '0.0000'
    }
    // 🔥 修复：确保是非负数
    const safeCost = Math.max(0, numCost);
    return safeCost < 0.01 ? safeCost.toFixed(6) : safeCost.toFixed(4)
  }

  const columns = [
    {
      title: <Tooltip title="用户ID"><UserOutlined /></Tooltip>,
      dataIndex: 'user_id',
      key: 'user_id',
      width: 100,
      render: (text: string) => (
        <Tooltip title={`用户ID: ${text}`}>
          <Text code style={{ fontSize: '11px' }}>
            {text.substring(0, 6)}...
          </Text>
        </Tooltip>
      )
    },
    {
      title: <Tooltip title="会话ID"><KeyOutlined /></Tooltip>,
      dataIndex: 'chat_id',
      key: 'chat_id',
      width: 100,
      render: (text: string) => (
        <Tooltip title={`会话ID: ${text}`}>
          <Text code style={{ fontSize: '11px' }}>
            {text.substring(0, 6)}...
          </Text>
        </Tooltip>
      )
    },
    {
      title: <Tooltip title="AI模型"><RobotOutlined /></Tooltip>,
      dataIndex: 'model',
      key: 'model',
      width: 150,
      render: (text: string) => (
        <Tooltip title={`模型: ${text}`}>
          <Tag color="blue" style={{ fontSize: '11px' }}>
            {text.split('/').pop()?.substring(0, 12)}...
          </Tag>
        </Tooltip>
      )
    },
    {
      title: '标题',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
      render: (text: string, record: Conversation) => (
        <div>
          <div>{text}</div>
          <div style={{ marginTop: 4 }}>
            {/* 🔥 Token数和费用小图标显示 */}
            <div style={{ marginBottom: 4 }}>
              <Tooltip title={`Token数: ${record.total_tokens?.toLocaleString() || 0}`}>
                <Tag icon={<TagOutlined />} color="blue" style={{ fontSize: '10px', marginRight: 4 }}>
                  {record.total_tokens ? `${(record.total_tokens / 1000).toFixed(1)}K` : '0'}
                </Tag>
              </Tooltip>

              {record.billing?.total_cost && record.billing.total_cost > 0 && (
                <Tooltip title={`费用: $${formatCost(record.billing.total_cost)} ${record.billing.currency || 'USD'}`}>
                  <Tag icon={<DollarOutlined />} color={record.billing.estimated ? "orange" : "green"} style={{ fontSize: '10px', marginRight: 4 }}>
                    {record.billing.estimated ? '~' : ''}${formatCost(record.billing.total_cost)}
                  </Tag>
                </Tooltip>
              )}
            </div>

            {/* 🔥 客户端标识 */}
            {record.client_type && (
              <Tooltip title={`客户端: ${record.client_type}`}>
                <Tag icon={<CloudOutlined />} color="cyan" style={{ fontSize: '11px', marginRight: 4 }}>
                  {record.client_type}
                </Tag>
              </Tooltip>
            )}

            {/* 🔥 画布类型标识 */}
            {record.canvas_type === 'real' && (
              <Tooltip title="真实画布模式">
                <Tag icon={<NodeIndexOutlined />} color="orange" style={{ fontSize: '11px', marginRight: 4 }}>
                  真实画布
                </Tag>
              </Tooltip>
            )}
            {record.canvas_type === 'virtual' && (
              <Tooltip title="虚拟画布模式">
                <Tag icon={<NodeIndexOutlined />} color="purple" style={{ fontSize: '11px', marginRight: 4 }}>
                  虚拟画布
                </Tag>
              </Tooltip>
            )}

            {/* 🔥 图片标识 */}
            {record.has_images && (
              <Tooltip title="包含图片内容">
                <Tag icon={<CameraOutlined />} color="green" style={{ fontSize: '11px', marginRight: 4 }}>
                  含图片
                </Tag>
              </Tooltip>
            )}

            {/* 🔥 上下文模式 */}
            {record.context_mode === 'canvas-based' && (
              <Tag color="geekblue" style={{ fontSize: '11px' }}>
                Canvas ({record.canvas_nodes_count || 0} 节点)
              </Tag>
            )}
            {record.context_mode === 'chat-based' && (
              <Tag color="green" style={{ fontSize: '11px' }}>
                传统聊天
              </Tag>
            )}
          </div>
        </div>
      )
    },
    {
      title: <Tooltip title="消息数量"><MessageOutlined /></Tooltip>,
      dataIndex: 'message_count',
      key: 'message_count',
      width: 80,
      align: 'center' as const,
      render: (value: number) => (
        <Tooltip title={`共 ${value} 条消息`}>
          <Tag color="blue" style={{ fontSize: '11px' }}>
            {value}
          </Tag>
        </Tooltip>
      )
    },
    {
      title: <Tooltip title="Token使用量"><TagOutlined /></Tooltip>,
      dataIndex: 'total_tokens',
      key: 'total_tokens',
      width: 100,
      align: 'right' as const,
      render: (value: number) => (
        <Tooltip title={`Token数: ${value?.toLocaleString() || 0}`}>
          <Text style={{ fontSize: '11px', color: '#666' }}>
            {value ? `${(value / 1000).toFixed(1)}K` : '0'}
          </Text>
        </Tooltip>
      )
    },
    {
      title: '费用',
      key: 'cost',
      width: 100,
      align: 'right' as const,
      render: (_: any, record: Conversation) => {
        const billingCost = record.billing?.total_cost || 0;
        const rawCost = record.total_cost || 0;
        const hasBoth = billingCost > 0 && rawCost > 0;
        const hasDifference = hasBoth && Math.abs(billingCost - rawCost) > 0.000001;

        // 🔥 修复：优先显示billing_records的费用（标准计费）
        if (billingCost > 0) {
          const currency = record.billing?.currency || 'USD';
          const isEstimated = record.billing?.estimated;

          const tooltipContent = (
            <div>
              <div>标准费用: ${formatCost(billingCost)} {currency} (billing_records)</div>
              {rawCost > 0 && (
                <div>原始费用: ${formatCost(rawCost)} USD (messages)</div>
              )}
              {hasDifference && (
                <div style={{ color: '#ff4d4f' }}>差异: ${formatCost(Math.abs(billingCost - rawCost))}</div>
              )}
              {isEstimated && <div style={{ color: '#fa8c16' }}>基于Token数量估算</div>}
            </div>
          );

          return (
            <div>
              <Tooltip title={tooltipContent}>
                <Tag
                  icon={<DollarOutlined />}
                  color={isEstimated ? "orange" : "blue"}
                >
                  {isEstimated ? '~' : ''}${formatCost(billingCost)}
                </Tag>
              </Tooltip>
              {hasDifference && (
                <Tag color="red" style={{ marginTop: 2, fontSize: '10px' }}>
                  差异
                </Tag>
              )}
            </div>
          )
        }

        // 🔥 降级：仅有原始费用数据
        if (rawCost > 0) {
          return (
            <Tooltip title={`原始费用: $${formatCost(rawCost)} USD (仅来自messages表)`}>
              <Tag
                icon={<DollarOutlined />}
                color="green"
              >
                ${formatCost(rawCost)}
              </Tag>
            </Tooltip>
          )
        }

        // 🔥 最后降级：显示Token数量
        if (record.total_tokens && record.total_tokens > 0) {
          return (
            <Tooltip title="暂无计费数据，显示Token数">
              <Tag color="orange">
                {record.total_tokens} tokens
              </Tag>
            </Tooltip>
          )
        }
        return <Text type="secondary">-</Text>
      }
    },
    {
      title: '最后消息',
      dataIndex: 'last_message',
      key: 'last_message',
      ellipsis: true,
      render: (text: string) => (
        <Paragraph ellipsis={{ rows: 1 }} style={{ margin: 0 }}>
          {text}
        </Paragraph>
      )
    },
    {
      title: <Tooltip title="创建时间"><ClockCircleOutlined /></Tooltip>,
      dataIndex: 'created_at',
      key: 'created_at',
      width: 120,
      render: (timestamp: number) => (
        <Tooltip title={dayjs(timestamp).format('YYYY-MM-DD HH:mm:ss')}>
          <Text style={{ fontSize: '11px', color: '#666' }}>
            {dayjs(timestamp).format('MM-DD HH:mm')}
          </Text>
        </Tooltip>
      )
    },
    {
      title: '操作',
      key: 'actions',
      width: 160,
      render: (_: any, record: Conversation) => (
        <Space>
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => fetchConversationDetail(record)}
          >
            查看
          </Button>
          <Popconfirm
            title="确认删除"
            description="删除会话将同时删除所有相关消息和计费记录，此操作不可恢复。确定要删除吗？"
            onConfirm={() => handleDeleteConversation(record)}
            okText="确定"
            cancelText="取消"
            okType="danger"
          >
            <Button
              type="link"
              icon={<DeleteOutlined />}
              danger
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ]

  return (
    <div style={{ padding: '0' }}>
      <Card title="最新会话" extra={
        <Space>
          <Button type="primary" icon={<ExportOutlined />} onClick={handleExport}>
            导出数据
          </Button>
        </Space>
      }>
        {/* 🔥 修复：增强筛选条件，支持上下文感知筛选 */}
        <div style={{ marginBottom: '16px' }}>
          {/* 第一行：基础筛选 */}
          <Space wrap style={{ marginBottom: '8px' }}>
            <Input
              placeholder="用户ID"
              value={filters.userId}
              onChange={e => setFilters(prev => ({ ...prev, userId: e.target.value }))}
              style={{ width: 150 }}
            />
            <Select
              placeholder="选择模型"
              value={filters.model || undefined}
              onChange={value => setFilters(prev => ({ ...prev, model: value || '' }))}
              style={{ width: 200 }}
              allowClear
            >
              <Option value="anthropic/claude-3.7-sonnet">Claude 3.7 Sonnet</Option>
              <Option value="anthropic/claude-3.5-haiku">Claude 3.5 Haiku</Option>
              <Option value="anthropic/claude-sonnet-4">Claude Sonnet 4</Option>
              <Option value="openai/gpt-4.1">GPT-4.1</Option>
              <Option value="openai/gpt-4o">GPT-4o</Option>
              <Option value="deepseek/deepseek-r1">DeepSeek R1</Option>
            </Select>
            <Input
              placeholder="搜索关键词"
              value={filters.keyword}
              onChange={e => setFilters(prev => ({ ...prev, keyword: e.target.value }))}
              style={{ width: 200 }}
              prefix={<SearchOutlined />}
            />
            <RangePicker
              value={filters.dateRange}
              onChange={value => setFilters(prev => ({ ...prev, dateRange: value as [dayjs.Dayjs, dayjs.Dayjs] | null }))}
              placeholder={['开始日期', '结束日期']}
            />
          </Space>

          {/* 第二行：上下文感知筛选 */}
          <Space wrap>
            <Select
              placeholder="上下文模式"
              value={filters.contextMode}
              onChange={value => setFilters(prev => ({ ...prev, contextMode: value }))}
              style={{ width: 150 }}
            >
              <Option value="all">全部模式</Option>
              <Option value="canvas-based">Canvas模式</Option>
              <Option value="chat-based">传统聊天</Option>
            </Select>

            <Select
              placeholder="画布类型"
              value={filters.canvasType}
              onChange={value => setFilters(prev => ({ ...prev, canvasType: value }))}
              style={{ width: 120 }}
            >
              <Option value="all">全部画布</Option>
              <Option value="real">真实画布</Option>
              <Option value="random">随机画布</Option>
            </Select>

            <Select
              placeholder="客户端类型"
              value={filters.clientType}
              onChange={value => setFilters(prev => ({ ...prev, clientType: value }))}
              style={{ width: 130 }}
            >
              <Option value="all">全部客户端</Option>
              <Option value="nextchat">NextChat</Option>
              <Option value="curl">cURL</Option>
              <Option value="browser">浏览器</Option>
              <Option value="python">Python</Option>
              <Option value="nodejs">Node.js</Option>
            </Select>

            <Select
              placeholder="图片内容"
              value={filters.hasImages}
              onChange={value => setFilters(prev => ({ ...prev, hasImages: value }))}
              style={{ width: 100 }}
            >
              <Option value="all">全部</Option>
              <Option value="yes">含图片</Option>
              <Option value="no">无图片</Option>
            </Select>

            <Select
              placeholder="排序方式"
              value={filters.sortBy}
              onChange={value => setFilters(prev => ({ ...prev, sortBy: value }))}
              style={{ width: 140 }}
            >
              <Option value="context_activity">上下文活跃度</Option>
              <Option value="updated_at">更新时间</Option>
              <Option value="canvas_nodes">Canvas节点数</Option>
              <Option value="tokens">Token数量</Option>
              <Option value="cost">费用</Option>
            </Select>

            <Select
              placeholder="排序顺序"
              value={filters.sortOrder}
              onChange={value => setFilters(prev => ({ ...prev, sortOrder: value }))}
              style={{ width: 100 }}
            >
              <Option value="desc">降序</Option>
              <Option value="asc">升序</Option>
            </Select>

            <Button
              icon={<ReloadOutlined />}
              onClick={() => fetchConversations(1)}
              title="刷新数据"
            />
          </Space>
        </div>

        {/* 会话列表 */}
        <Table
          columns={columns}
          dataSource={conversations}
          loading={loading}
          rowKey="id"
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`
          }}
          onChange={(paginationInfo) => {
            fetchConversations(paginationInfo.current)
          }}
        />
      </Card>

      {/* 会话详情抽屉 */}
      <Drawer
        title={`会话详情 - ${selectedConversation?.title}`}
        placement="right"
        width={800}
        open={detailVisible}
        onClose={() => setDetailVisible(false)}
        loading={detailLoading}
      >
        {selectedConversation && (
          <div>
            {/* 会话信息 */}
            <Card size="small" style={{ marginBottom: '16px' }}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <div><strong>用户ID:</strong> <Text code>{selectedConversation.user_id}</Text></div>
                <div><strong>会话ID:</strong> <Text code>{selectedConversation.chat_id}</Text></div>
                <div><strong>模型:</strong> <Tag color="blue">{selectedConversation.model}</Tag></div>
                <div><strong>消息数:</strong> {selectedConversation.message_count}</div>

                {/* 🔥 修复：Token数和费用以小图标方式显示 */}
                <div>
                  <strong>Token数:</strong>
                  <Tooltip title={`Token数: ${selectedConversation.total_tokens?.toLocaleString() || 0}`}>
                    <Tag icon={<TagOutlined />} color="blue" style={{ marginLeft: 8 }}>
                      {selectedConversation.total_tokens?.toLocaleString() || 0} tokens
                    </Tag>
                  </Tooltip>
                </div>

                {/* 🔥 优先显示来自messages表的费用数据 */}
                {((selectedConversation.total_cost && selectedConversation.total_cost > 0) || (selectedConversation.billing && selectedConversation.billing.total_cost > 0)) && (
                  <div>
                    <strong>总费用:</strong>
                    {selectedConversation.total_cost && selectedConversation.total_cost > 0 ? (
                      <Tooltip title={`费用: $${formatCost(selectedConversation.total_cost)} USD (来自消息表)`}>
                        <Tag
                          icon={<DollarOutlined />}
                          color="green"
                          style={{ marginLeft: 8 }}
                        >
                          ${formatCost(selectedConversation.total_cost)} USD
                        </Tag>
                      </Tooltip>
                    ) : selectedConversation.billing && selectedConversation.billing.total_cost > 0 && (
                      <Tooltip title={`费用: $${formatCost(selectedConversation.billing.total_cost)} ${selectedConversation.billing.currency || 'USD'} (来自计费表)`}>
                        <Tag
                          icon={<DollarOutlined />}
                          color={selectedConversation.billing.estimated ? "orange" : "blue"}
                          style={{ marginLeft: 8 }}
                        >
                          {selectedConversation.billing.estimated ? '~' : ''}${formatCost(selectedConversation.billing.total_cost)} {selectedConversation.billing.currency || 'USD'}
                        </Tag>
                      </Tooltip>
                    )}
                  </div>
                )}

                <div><strong>创建时间:</strong> {dayjs(selectedConversation.created_at).format('YYYY-MM-DD HH:mm:ss')}</div>
              </Space>
            </Card>

            {/* 消息列表 */}
            <div style={{ maxHeight: 'calc(100vh - 300px)', overflowY: 'auto' }}>
              {messages.map((message) => (
                <Card
                  key={message.id}
                  size="small"
                  style={{ marginBottom: '8px' }}
                  title={
                    <Space>
                      <Tag color={message.role === 'user' ? 'green' : 'blue'}>
                        {message.role === 'user' ? '用户' : 'AI助手'}
                      </Tag>
                      <Text type="secondary">
                        {dayjs(message.created_at).format('HH:mm:ss')}
                      </Text>

                      {/* 🔥 修复：Token数以小图标方式显示 */}
                      {((message.total_tokens && message.total_tokens > 0) || message.tokens > 0) && (
                        <Tooltip title={`Token数: ${message.total_tokens || message.tokens}`}>
                          <Tag icon={<TagOutlined />} color="blue">
                            {message.total_tokens || message.tokens} tokens
                          </Tag>
                        </Tooltip>
                      )}

                      {/* 🔥 修复：消息费用以小图标方式显示（优先使用消息表中的费用数据） */}
                      {((message.total_cost && message.total_cost > 0) || (message.billing && message.billing.total_cost > 0)) && (
                        <Tooltip title={
                          message.total_cost && message.total_cost > 0
                            ? `消息费用: $${formatCost(message.total_cost)} (来自消息表)`
                            : `消息费用: $${formatCost(message.billing?.total_cost || 0)} (来自计费表)`
                        }>
                          <Tag icon={<DollarOutlined />} color={message.total_cost && message.total_cost > 0 ? "green" : "blue"}>
                            ${formatCost(message.total_cost && message.total_cost > 0 ? message.total_cost : (message.billing?.total_cost || 0))}
                          </Tag>
                        </Tooltip>
                      )}
                    </Space>
                  }
                >
                  <MessageContent
                    content={message.parsed_content || message.content}
                    role={message.role}
                    isCanvasNode={message.is_canvas_node}
                    resultId={message.result_id}
                  />
                </Card>
              ))}
            </div>
          </div>
        )}
      </Drawer>
    </div>
  )
}

export default LatestSessionsPage
